import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControlLabel, Checkbox, Alert, CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../contexts/AuthContext';
import { InferenceNode } from '../components/devices/types';

const EMPTY_FORM = {
  name: '',
  displayName: '',
  baseUrl: '',
  isActive: true,
};

function NodeRow({ node, onEdit, onDelete }: {
  node: InferenceNode;
  onEdit: (n: InferenceNode) => void;
  onDelete: (n: InferenceNode) => void;
}) {
  return (
    <TableRow sx={{ opacity: node.isActive ? 1 : 0.5 }}>
      <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{node.name}</Typography></TableCell>
      <TableCell>{node.displayName}</TableCell>
      <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{node.baseUrl}</Typography></TableCell>
      <TableCell>
        <Chip
          label={node.isActive ? 'Active' : 'Inactive'}
          color={node.isActive ? 'success' : 'default'}
          size="small"
        />
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(node)}><EditIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title={node.isActive ? 'Deactivate' : 'Already inactive'}>
          <span>
            <IconButton size="small" color="error" onClick={() => onDelete(node)} disabled={!node.isActive}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default function InferenceNodes() {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;

  const [nodes, setNodes] = useState<InferenceNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Edit/Create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InferenceNode | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState<InferenceNode | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { load(); }, [showInactive]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/inference-nodes?includeInactive=${showInactive}`);
      if (!res.ok) throw new Error(`${res.status}`);
      setNodes(await res.json());
    } catch (e: any) {
      setError('Failed to load inference nodes.');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setSaveError('');
    setDialogOpen(true);
  }

  function openEdit(n: InferenceNode) {
    setEditing(n);
    setForm({ name: n.name, displayName: n.displayName, baseUrl: n.baseUrl, isActive: n.isActive });
    setSaveError('');
    setDialogOpen(true);
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      let res: Response;
      if (editing) {
        res = await fetch(`/api/inference-nodes/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(form),
        });
      } else {
        res = await fetch('/api/inference-nodes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(form),
        });
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `${res.status}`);
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setSaveError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/inference-nodes/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      setError('Failed to deactivate node.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Inference Nodes</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label={<Typography variant="body2">Show inactive</Typography>}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Node</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Base URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No inference nodes found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                nodes.map((n) => (
                  <NodeRow key={n.id} node={n} onEdit={openEdit} onDelete={setDeleteTarget} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Inference Node' : 'Add Inference Node'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}
            <TextField
              label="Name (key)"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              fullWidth
              size="small"
              helperText="Unique machine-readable key, e.g. laptop-rtx4070"
            />
            <TextField
              label="Display Name"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Base URL"
              value={form.baseUrl}
              onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
              required
              fullWidth
              size="small"
              helperText="Tailscale-reachable inference endpoint, e.g. http://100.x.x.x:8044"
              sx={{ '& input': { fontFamily: 'monospace', fontSize: 13 } }}
            />
            {editing && (
              <FormControlLabel
                control={<Checkbox checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />}
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.name.trim() || !form.displayName.trim() || !form.baseUrl.trim()}
          >
            {saving ? 'Saving…' : editing ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirm dialog */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Node</DialogTitle>
        <DialogContent>
          <Typography>
            Deactivate <strong>{deleteTarget?.displayName}</strong>? It will be hidden from dropdowns but not deleted.
            Devices assigned to it will retain the reference.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
